import { T } from "@/i18n";
import { cn } from "@/lib/utils";
import { RouterPararms, RouterPaths, RouterSearch } from "@/models";
import { Link, Outlet } from "@tanstack/react-router";
import { Plus, TableIcon } from "lucide-react";
import { BreadcrumbOptions } from "../breadcrumbs";
import { NavActions, NavItems } from "../nav-actions";
import { PageHeader } from "../page-header";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { useTranslation } from "react-i18next";
import { Toggle } from "../ui/toggle";
import { useState } from "react";
import { ContentMinimap } from "../table-minimap";

const loadingItems = new Array(6).fill(0);

function Loading() {
  return loadingItems.map((_, i) => (
    <Skeleton key={i} className="h-60 w-full" />
  ));
}

interface DomainListProps<T, TP extends RouterPaths = RouterPaths> {
  domain: string;
  data: T[] | undefined;
  children?: React.ReactNode;
  isLoading: boolean;
  breadcrumbs?: BreadcrumbOptions[];
  renderItem: (item: T, index: number, table: boolean) => React.ReactNode;
  tableOptions?: {
    hasActions?: boolean;
    columns: Array<{
      header: string;
      headerClassName?: string;
    }>;
  };
  onNew?: () => void;
  newLink?: TP;
  newLinkParams?: RouterPararms<TP>;
  newLinkSearch?: RouterSearch<TP>;
  hideNavActions?: boolean;
  type?: "grid" | "list" | "table";
  navActions?: NavItems;
  navLeading?: React.ReactNode;
  noOutlet?: boolean;
  hidePageHeader?: boolean;
  titleKey?: string;
}

export function DomainList<T>({
  data,
  domain,
  isLoading,
  breadcrumbs,
  renderItem,
  newLink,
  onNew,
  newLinkParams,
  newLinkSearch,
  children,
  hideNavActions,
  type: initialType = "grid",
  navActions,
  navLeading,
  noOutlet = false,
  tableOptions = { columns: [] },
  hidePageHeader = false,
  titleKey,
}: DomainListProps<T>) {
  const { t } = useTranslation();
  const [type, setType] = useState(initialType);
  return (
    <>
      {(breadcrumbs || !hidePageHeader) && (
        <PageHeader breadcrumbs={breadcrumbs ?? []} />
      )}
      <div className="flex flex-1 flex-col gap-4 p-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-primary">
            <T tKey={`${domain}.${titleKey || "title"}`} />
          </h1>
          {navLeading}
          {hideNavActions ? (
            <></>
          ) : (
            <div className="itens-center flex gap-4">
              {newLink && (
                <Button asChild type="button">
                  <Link
                    to={newLink}
                    params={newLinkParams}
                    search={newLinkSearch}
                  >
                    <Plus />
                    <T tKey={`${domain}.contextMenu.new`} />
                  </Link>
                </Button>
              )}
              {onNew && (
                <Button onClick={() => onNew()}>
                  {children}
                  <Plus />
                  <T tKey={`${domain}.contextMenu.new`} />
                </Button>
              )}

              {type !== "list" ? (
                <Toggle
                  aria-label={t("common.toggleTableView")}
                  pressed={type === "table"}
                  onPressedChange={() => {
                    setType((prev) => (prev === "table" ? "grid" : "table"));
                  }}
                >
                  <TableIcon />
                </Toggle>
              ) : (
                <></>
              )}
              {navActions?.length ? <NavActions items={navActions} /> : <></>}
            </div>
          )}
        </div>

        {type === "table" ? (
          <ContentMinimap
            count={
              tableOptions.columns.length + (tableOptions.hasActions ? 1 : 0)
            }
          >
            <Table wrapperClassName="overflow-x-hidden">
              <TableHeader>
                <TableRow>
                  {tableOptions.columns.map((col) => (
                    <TableHead
                      key={col.header}
                      className={cn(col.headerClassName, "whitespace-nowrap")}
                    >
                      {t(col.header)}
                    </TableHead>
                  ))}
                  <TableHead>
                    <T tKey="common.tableActions" />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.map((el, index) => renderItem(el, index, true))}
                {isLoading ? (
                  loadingItems.map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={tableOptions.columns.length}>
                        <Skeleton className="h-7 w-full" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <></>
                )}
              </TableBody>
            </Table>
          </ContentMinimap>
        ) : (
          <div
            className={cn(
              "grid place-items-stretch gap-4",
              type === "grid" &&
                "auto-rows-min md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4",
            )}
          >
            {children ?? data?.map((el, index) => renderItem(el, index, false))}
            {isLoading ? <Loading /> : <></>}
          </div>
        )}
      </div>
      {noOutlet ? <></> : <Outlet />}
    </>
  );
}
